import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import * as Sentry from "@sentry/angular";

import { APP_VERSION } from './app/version';
import { scrubSentryEvent, scrubSentrySpan } from './shared/util/sentry-scrub';
import { chunkLoadRecovery, listenForZoneUnhandledRejections, reloadsForSentryEvent } from './shared/util/chunk-load-recovery';

/* eslint-disable no-restricted-globals -- runs before Angular bootstrap, no Platform service available */

// Unauthenticated visits to app.tidy.com get redirected to www.tidy.com,
// where the customer session and login flow live. Subdomains don't share
// storage, so without this a shared link like app.tidy.com/#/booking/123
// bounces the recipient to login even though they're authenticated on www,
// and first-time visitors to app.tidy.com are sent away from the canonical
// login host. The hash route is converted to a path (www.tidy.com uses path
// routing, not hash routing) so deep links resolve after authentication.
const shouldRedirectToWww = (() => {
  if (window.location.hostname !== 'app.tidy.com') {return false;}
  if (localStorage.getItem('tidy_token')) {return false;}
  const hash = window.location.hash;
  const pathFromHash = hash && hash.startsWith('#/') ? hash.substring(1) : '';
  const path = pathFromHash || window.location.pathname;
  window.location.replace(
    `https://www.tidy.com${path}${window.location.search}`
  );
  return true;
})();

if (!shouldRedirectToWww) {

// Redirect hash URLs to path URLs before Angular initializes (backward compatibility)
if (window.location.hash && window.location.hash.startsWith('#/')) {
  const pathFromHash = window.location.hash.substring(2);
  window.history.replaceState(null, '', `/${  pathFromHash  }${window.location.search}`);
}

if (environment.production) {
  const script = document.createElement('script');
  script.innerHTML = `!function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', '${environment.facebook_pixel_id}');
  fbq('track', 'PageView');`;
  document.head.appendChild(script);
}

enableProdMode();

// Chunk-load errors raised outside Angular (such as Ionic's component loader) never reach the ErrorHandler.
// Zone.js keeps those rejections from reaching the window's `unhandledrejection` event, so hook Zone too.
listenForZoneUnhandledRejections((window as unknown as { Zone?: Parameters<typeof listenForZoneUnhandledRejections>[0] }).Zone);
window.addEventListener('unhandledrejection', event => chunkLoadRecovery.recover(event.reason));
window.addEventListener('error', event => chunkLoadRecovery.recover(event.error ?? event.message));

// Only initialize Sentry in production
if (environment.production) {
  Sentry.init({
    environment: environment.loggly_tag,
    // App version from package.json, so triage can tell current builds from stale bundles.
    release: APP_VERSION,
    dsn: "https://c3f6899fc1e19b4833a20387a3e0c426@o4508852947845120.ingest.us.sentry.io/4508887200825344",
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.05,
    tracePropagationTargets: [/^https:\/\/.*\.tidy\.com/],
    beforeSend: (event, hint) => reloadsForSentryEvent(event, hint) ? null : scrubSentryEvent(event),
    beforeSendTransaction: event => scrubSentryEvent(event),
    beforeSendSpan: span => scrubSentrySpan(span),
  });
}

platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
  .catch(_err => {
    // Bootstrap error handled by Sentry
  });

}
/* eslint-enable no-restricted-globals */
